import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  InteractionObjectType,
  InteractionType,
  OrganisationTypeConstants,
  ProjectAccessConstants,
  ProjectPermissionConstants,
  ProjectStatusConstants,
  UserType,
} from "../../constants";
import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { NavigationSearchParams, PillData } from "../../models";
import { ProjectContext } from "../../route/shared/projects/project/ProjectContext";
import { resumeActivity } from "../../route/shared/utils";
import {
  GetProjectDetailsResponse,
  GetProjectDetailsResponse as GetPublicProjectDetailsResponse,
  searchActivityHistory,
  SearchActivityHistoryResponse,
  searchInteractions,
  SearchProjectOrganisationInvitations,
  SearchProjectOrganisationInvitationsRequest,
  SearchProjectOrganisationInvitationsResponse,
  searchWorkflowTasks,
} from "../../service/query";
import { Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { isPollingAbortedError, pollAsync } from "../../utils";
import { getProjectAccessDetailsRoute, getProjectAuditHistoryRoute } from "../../utils/routes";
import { dataGridMapFilterCriteria, ProfileMenuItemType, Toast } from "../../widget";

const PENDING_DEVELOPER_INVITATION_POLL_INTERVAL_MS = 15000;

interface UseAssetManagerProjectCardWithTabsTemplateReturnData {
  quickActionsMenuOptionsList: ProfileMenuItemType[];
  setShowQuickActionMenu: Dispatch<SetStateAction<boolean>>;
  showQuickActionMenu: boolean;
  setShowInviteDeveloperModal: Dispatch<SetStateAction<boolean>>;
  showInviteDeveloperModal: boolean;
  setShowPublishProjectModal: Dispatch<SetStateAction<boolean>>;
  showPublishProjectModal: boolean;
  setShowDeleteProjectModal: Dispatch<SetStateAction<boolean>>;
  showDeleteProjectModal: boolean;
  setShowMultiDraftModal: Dispatch<SetStateAction<boolean>>;
  showMultiDraftModal: boolean;
  setShowVersionConflictModal: Dispatch<SetStateAction<boolean>>;
  showVersionConflictModal: boolean;
  setShowNotesPanel: Dispatch<SetStateAction<boolean>>;
  showNotesPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  showUpdateStatusModal: boolean;
  setShowUpdateStatusModal: Dispatch<SetStateAction<boolean>>;
  showCancelProjectModal: boolean;
  setShowCancelProjectModal: Dispatch<SetStateAction<boolean>>;
  showRequestProjectAccessModal: boolean;
  setShowRequestProjectAccessModal: Dispatch<SetStateAction<boolean>>;
  showTagsPanel: boolean;
  draftActivityHistory?: SearchActivityHistoryResponse;
  newVersionActivityHistory?: SearchActivityHistoryResponse;
  draftActivityHistoryUuid?: string;
  tags: PillData[];
  navigateToDraftActivity: () => void;
  resumeDraft: (uuid: string, draftActivityHistory: SearchActivityHistoryResponse) => void;
  shouldRefreshProjectDetails: () => void;
  shouldRefreshTags: () => void;
  currentUserType: UserType;
  isGroupProject: boolean;
  projectDetails: GetProjectDetailsResponse | undefined;
  publicProjectDetails: GetPublicProjectDetailsResponse | undefined;
  nextMilestone: string | undefined;
  pendingDeveloperInvitation?: SearchProjectOrganisationInvitationsResponse["results"][number];
  isPendingDeveloperInvitationLoading: boolean;
  navigateToPendingDeveloperInvitation: () => void;
  shouldRefreshPendingDeveloperInvitation: (shouldPollForInvitation?: boolean) => void;
  pendingProjectAccessRequest?: SearchProjectOrganisationInvitationsResponse["results"][number];
  isPendingProjectAccessRequestLoading: boolean;
  navigateToPendingProjectAccessRequest: () => void;
  shouldRefreshPendingProjectAccessRequest: (shouldPollForRequest?: boolean) => void;
}

export const useAssetManagerProjectCardWithTabsTemplate = (): UseAssetManagerProjectCardWithTabsTemplateReturnData => {
  const navigate = useNavigate();

  const { currentUserType, currentOrganisationUuid } = useAuth();
  const { projectDetails, publicProjectDetails, hasProjectPermission, refreshActivities, shouldRefreshProjectDetails } =
    useContext(ProjectContext);

  const [showQuickActionMenu, setShowQuickActionMenu] = useState(false);
  const [showInviteDeveloperModal, setShowInviteDeveloperModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showPublishProjectModal, setShowPublishProjectModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showCancelProjectModal, setShowCancelProjectModal] = useState(false);
  const [showRequestProjectAccessModal, setShowRequestProjectAccessModal] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [showMultiDraftModal, setShowMultiDraftModal] = useState(false);
  const [draftActivityHistory, setDraftActivityHistory] = useState<SearchActivityHistoryResponse>();
  const [tags, setTags] = useState<PillData[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);
  // Version conflict modal
  const [showVersionConflictModal, setShowVersionConflictModal] = useState(false);
  const [newVersionActivityHistory, setNewVersionActivityHistory] = useState<SearchActivityHistoryResponse>();
  const [draftActivityHistoryUuid, setDraftActivityHistoryUuid] = useState<string>();
  const [nextMilestone, setNextMilestone] = useState<string>();
  const [pendingDeveloperInvitation, setPendingDeveloperInvitation] =
    useState<SearchProjectOrganisationInvitationsResponse["results"][number]>();
  const [isPendingDeveloperInvitationLoading, setIsPendingDeveloperInvitationLoading] = useState(true);
  const [pendingProjectAccessRequest, setPendingProjectAccessRequest] =
    useState<SearchProjectOrganisationInvitationsResponse["results"][number]>();
  const [isPendingProjectAccessRequestLoading, setIsPendingProjectAccessRequestLoading] = useState(true);
  // Cancels the active polling chain so a replaced or unmounted screen does not keep polling in the background.
  const pendingDeveloperInvitationAbortControllerRef = useRef<AbortController | null>(null);
  const pendingProjectAccessRequestAbortControllerRef = useRef<AbortController | null>(null);
  // Guards state updates from stale async responses when a newer refresh has already started.
  const pendingDeveloperInvitationRefreshIdRef = useRef(0);
  const pendingProjectAccessRequestRefreshIdRef = useRef(0);
  const quickActionsMenuOptionsList: ProfileMenuItemType[] = [];

  if (projectDetails?.status === "Pre-development" || projectDetails?.status === "Draft") {
    quickActionsMenuOptionsList.push({
      id: 1,
      value: "Delete project",
      action: () => setShowDeleteProjectModal(true),
    });
  }

  if (projectDetails?.status === ProjectStatusConstants.DRAFT) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "Publish project",
      action: () => setShowPublishProjectModal(true),
    });
  }

  if (
    [
      ProjectStatusConstants.PRE_DEVELOPMENT,
      ProjectStatusConstants.UNDER_DEVELOPMENT,
      ProjectStatusConstants.VALIDATED,
    ].includes(projectDetails?.status || "") &&
    hasProjectPermission(ProjectPermissionConstants.WRITE_PROJECT_DETAILS)
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: `Update ${ProjectStatusConstants.REGISTRY_STATUS_LABEL}`,
      action: () => setShowUpdateStatusModal(true),
    });
  }

  if (
    [
      ProjectStatusConstants.UNDER_DEVELOPMENT,
      ProjectStatusConstants.VALIDATED,
      ProjectStatusConstants.VERIFIED,
    ].includes(projectDetails?.status || "") &&
    hasProjectPermission(ProjectPermissionConstants.DELETE_PROJECT)
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "Cancel project",
      action: () => setShowCancelProjectModal(true),
    });
  }

  if (
    hasProjectPermission(ProjectPermissionConstants.READ_ALL_PROJECT_AUDIT) ||
    hasProjectPermission(ProjectPermissionConstants.READ_OWN_ORG_PROJECT_AUDIT)
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "View audit history",
      action: () => navigate(getProjectAuditHistoryRoute(projectDetails?.uuid || "", currentUserType)),
    });
  }

  quickActionsMenuOptionsList.push({
    id: quickActionsMenuOptionsList.length + 1,
    value: "View notes",
    action: () => setShowNotesPanel(true),
  });

  const shouldRefreshTags = (): void => setRefreshTags((prev) => !prev);

  const resumeDraft = async (
    _draftActivityHistoryUuid: string,
    localDraftActivityHistory: SearchActivityHistoryResponse
  ): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const activityHistory = localDraftActivityHistory.results.find((x) => x.uuid === _draftActivityHistoryUuid)!;

    await resumeActivity(
      navigate,
      setDraftActivityHistoryUuid,
      setNewVersionActivityHistory,
      setShowVersionConflictModal,
      activityHistory.activity.uuid,
      activityHistory.uuid,
      activityHistory.versionNumber,
      true
    );
  };

  const navigateToDraftActivity = async (): Promise<void> => {
    if (draftActivityHistory?.results) {
      if (draftActivityHistory.results.length > 1) {
        setShowMultiDraftModal(true);
      } else {
        await resumeActivity(
          navigate,
          setDraftActivityHistoryUuid,
          setNewVersionActivityHistory,
          setShowVersionConflictModal,
          draftActivityHistory.results[0].activity.uuid,
          draftActivityHistory.results[0].uuid,
          draftActivityHistory.results[0].versionNumber,
          true
        );
      }
    }
  };

  const fetchDraftActivities = useCallback(
    async (uuid: string) => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.activity = {
        project: {
          uuid: {
            operator: "eq",
            value: uuid,
          },
        },
      };

      filterCriteria.isDraft = {
        operator: "eq",
        value: true,
      };

      const response = await searchActivityHistory({
        activityUuid: null,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 10,
        },

        filter: { results: filterCriteria },
      });

      if (response.status === Status.Success && response.data && response.data.results.length) {
        setDraftActivityHistory(response.data);
      }
    },
    [refreshActivities, projectDetails?.uuid]
  );

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      }).then((response) => {
        if (response.data?.results != null) {
          setTags(
            response.data.results.map(
              (x) =>
                ({
                  key: x.uuid,
                  label: JSON.parse(x.data).name,
                }) as PillData
            )
          );
        }
      });
    },
    [projectDetails?.uuid, currentOrganisationUuid, refreshTags]
  );

  const fetchPendingDeveloperInvitation = useCallback(async (): Promise<
    SearchProjectOrganisationInvitationsResponse["results"][number] | undefined
  > => {
    if (!projectDetails?.uuid || !currentOrganisationUuid || projectDetails.developer?.uuid) {
      return undefined;
    }

    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.project = {
      uuid: {
        operator: "eq",
        value: projectDetails.uuid,
      },
    };

    filterCriteria.role = {
      operator: "eq",
      value: OrganisationTypeConstants.DEVELOPER,
    };

    filterCriteria.sourceOrganisation = {
      uuid: {
        operator: "eq",
        value: currentOrganisationUuid,
      },
    };

    filterCriteria.calculatedStatus = {
      operator: "eq",
      value: ProjectAccessConstants.STATUS_SENT,
    };

    const response = await SearchProjectOrganisationInvitations({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: filterCriteria,
      },
      sort: [{ key: "results.sentDate", direction: "desc" }],
    });

    return response.data?.results?.[0];
  }, [currentOrganisationUuid, projectDetails?.developer?.uuid, projectDetails?.uuid]);

  const fetchPendingProjectAccessRequest = useCallback(async (): Promise<
    SearchProjectOrganisationInvitationsResponse["results"][number] | undefined
  > => {
    setIsPendingProjectAccessRequestLoading(true);

    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.project = {
      uuid: {
        operator: "eq",
        value: publicProjectDetails?.uuid,
      },
    };

    filterCriteria.sourceOrganisation = {
      uuid: {
        operator: "eq",
        value: currentOrganisationUuid,
      },
    };

    filterCriteria.targetOrganisation = {
      uuid: {
        operator: "eq",
        value: publicProjectDetails?.responsibleOwnerOrganisationUuid,
      },
    };

    filterCriteria.calculatedStatus = {
      operator: "eq",
      value: ProjectAccessConstants.STATUS_SENT,
    };

    const response = await SearchProjectOrganisationInvitations({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: filterCriteria as unknown as NonNullable<
          NonNullable<SearchProjectOrganisationInvitationsRequest["filter"]>["results"]
        >,
      },
      sort: [{ key: "results.sentDate", direction: "desc" }],
    });

    return response.data?.results?.[0];
  }, [currentOrganisationUuid, publicProjectDetails?.responsibleOwnerOrganisationUuid, publicProjectDetails?.uuid]);

  const navigateToPendingProjectAccessRequest = useCallback((): void => {
    if (pendingProjectAccessRequest?.uuid && publicProjectDetails?.uuid) {
      navigate(
        `${getProjectAccessDetailsRoute(
          pendingProjectAccessRequest.uuid,
          currentUserType,
          publicProjectDetails.uuid
        )}?${NavigationSearchParams.projectAccessTab}=true`
      );
    }
  }, [currentUserType, navigate, pendingProjectAccessRequest?.uuid, publicProjectDetails?.uuid]);

  const shouldRefreshPendingDeveloperInvitation = useCallback(
    (shouldPollForInvitation = false): void => {
      const refreshId = pendingDeveloperInvitationRefreshIdRef.current + 1;
      pendingDeveloperInvitationRefreshIdRef.current = refreshId;
      // Stop any earlier polling chain before starting the next one.
      pendingDeveloperInvitationAbortControllerRef.current?.abort();

      const abortController = new AbortController();
      pendingDeveloperInvitationAbortControllerRef.current = abortController;

      if (!projectDetails?.uuid || !currentOrganisationUuid || projectDetails.developer?.uuid) {
        setPendingDeveloperInvitation(undefined);
        setIsPendingDeveloperInvitationLoading(false);
        return;
      }

      setIsPendingDeveloperInvitationLoading(true);

      // New developer creation is eventually consistent, so that path opts into polling.
      const invitationRequest = shouldPollForInvitation
        ? pollAsync(fetchPendingDeveloperInvitation, {
            intervalMs: PENDING_DEVELOPER_INVITATION_POLL_INTERVAL_MS,
            signal: abortController.signal,
          })
        : fetchPendingDeveloperInvitation();

      invitationRequest
        .then((invitation) => {
          // Ignore responses from a superseded refresh or an explicitly aborted polling chain.
          if (pendingDeveloperInvitationRefreshIdRef.current !== refreshId || abortController.signal.aborted) {
            return;
          }

          setPendingDeveloperInvitation(invitation);
          setIsPendingDeveloperInvitationLoading(false);
        })
        .catch((error: unknown) => {
          // Aborted polls are expected when a new refresh starts or the component unmounts.
          if (pendingDeveloperInvitationRefreshIdRef.current !== refreshId || isPollingAbortedError(error)) {
            return;
          }

          setPendingDeveloperInvitation(undefined);
          setIsPendingDeveloperInvitationLoading(false);
          Toast.error({ message: "Unable to load developer invitation" });
        });
    },
    [currentOrganisationUuid, fetchPendingDeveloperInvitation, projectDetails?.developer?.uuid, projectDetails?.uuid]
  );

  const shouldRefreshPendingProjectAccessRequest = useCallback(
    (shouldPollForRequest = false): void => {
      const refreshId = pendingProjectAccessRequestRefreshIdRef.current + 1;
      pendingProjectAccessRequestRefreshIdRef.current = refreshId;
      pendingProjectAccessRequestAbortControllerRef.current?.abort();

      const abortController = new AbortController();
      pendingProjectAccessRequestAbortControllerRef.current = abortController;

      if (
        !publicProjectDetails?.uuid ||
        !currentOrganisationUuid ||
        !publicProjectDetails.responsibleOwnerOrganisationUuid
      ) {
        setPendingProjectAccessRequest(undefined);
        setIsPendingProjectAccessRequestLoading(false);
        return;
      }

      setIsPendingProjectAccessRequestLoading(true);

      const projectAccessRequest = shouldPollForRequest
        ? pollAsync(fetchPendingProjectAccessRequest, {
            intervalMs: PENDING_DEVELOPER_INVITATION_POLL_INTERVAL_MS,
            signal: abortController.signal,
          })
        : fetchPendingProjectAccessRequest();

      projectAccessRequest
        .then((request) => {
          if (pendingProjectAccessRequestRefreshIdRef.current !== refreshId || abortController.signal.aborted) {
            return;
          }

          setPendingProjectAccessRequest(request);
          setIsPendingProjectAccessRequestLoading(false);
        })
        .catch((error: unknown) => {
          if (pendingProjectAccessRequestRefreshIdRef.current !== refreshId || isPollingAbortedError(error)) {
            return;
          }

          setPendingProjectAccessRequest(undefined);
          setIsPendingProjectAccessRequestLoading(false);
          Toast.error({ message: "Unable to load project access request" });
        });
    },
    [
      currentOrganisationUuid,
      fetchPendingProjectAccessRequest,
      publicProjectDetails?.responsibleOwnerOrganisationUuid,
      publicProjectDetails?.uuid,
    ]
  );

  const navigateToPendingDeveloperInvitation = useCallback((): void => {
    if (pendingDeveloperInvitation?.uuid && projectDetails?.uuid) {
      navigate(
        `${getProjectAccessDetailsRoute(
          pendingDeveloperInvitation.uuid,
          currentUserType,
          projectDetails.uuid
        )}?${NavigationSearchParams.projectAccessTab}=true`
      );
    }
  }, [currentUserType, navigate, pendingDeveloperInvitation?.uuid, projectDetails?.uuid]);

  useEffect(() => {
    if (projectDetails?.uuid) {
      fetchDraftActivities(projectDetails.uuid);
    }
  }, [projectDetails?.uuid, refreshActivities]);

  // TODO: Show tags if not readProject?
  useEffect(() => {
    if (projectDetails?.uuid && currentOrganisationUuid) {
      fetchTags(projectDetails.uuid, currentOrganisationUuid);
    }
  }, [currentUserType, projectDetails?.uuid, currentOrganisationUuid, refreshTags]);

  useEffect(() => {
    shouldRefreshPendingDeveloperInvitation();
  }, [shouldRefreshPendingDeveloperInvitation]);

  useEffect(() => {
    shouldRefreshPendingProjectAccessRequest();
  }, [shouldRefreshPendingProjectAccessRequest]);

  // Cleanup on unmount to stop any ongoing polling.
  useEffect(
    () => () => {
      pendingDeveloperInvitationAbortControllerRef.current?.abort();
      pendingProjectAccessRequestAbortControllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    const getAndSetNextMilestone = async (): Promise<void> => {
      if (!projectDetails?.workflowUuid) {
        setNextMilestone(undefined);
        return;
      }

      const milestonesRes = await searchWorkflowTasks({
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 1,
        },
        filter: {
          results: {
            workflowUuid: {
              operator: "eq",
              value: projectDetails.workflowUuid,
            },
            isMilestone: {
              operator: "eq",
              value: true,
            },
            deadlineDate: {
              operator: "neq",
              value: null,
            },
            status: {
              operator: "neq",
              value: TaskConstants.TASK_STATUS_COMPLETE,
            },
          },
        },
        sort: [{ key: "results.deadlineDate", direction: "asc" }],
      });

      if (milestonesRes.status === Status.Success && milestonesRes.data) {
        setNextMilestone(milestonesRes.data.results[0]?.name);
      } else {
        setNextMilestone(undefined);
      }
    };

    getAndSetNextMilestone().catch(() => {
      setNextMilestone(undefined);
    });
  }, [projectDetails?.workflowUuid]);

  return {
    quickActionsMenuOptionsList,
    setShowQuickActionMenu,
    showQuickActionMenu,
    setShowInviteDeveloperModal,
    showInviteDeveloperModal,
    setShowPublishProjectModal,
    showPublishProjectModal,
    setShowDeleteProjectModal,
    showDeleteProjectModal,
    setShowMultiDraftModal,
    showMultiDraftModal,
    setShowVersionConflictModal,
    showVersionConflictModal,
    showNotesPanel,
    setShowNotesPanel,
    showTagsPanel,
    setShowTagsPanel,
    showUpdateStatusModal,
    setShowUpdateStatusModal,
    showCancelProjectModal,
    setShowCancelProjectModal,
    showRequestProjectAccessModal,
    setShowRequestProjectAccessModal,
    draftActivityHistory,
    newVersionActivityHistory,
    draftActivityHistoryUuid,
    navigateToDraftActivity,
    tags,
    resumeDraft,
    shouldRefreshProjectDetails,
    shouldRefreshTags,
    currentUserType,
    isGroupProject: projectDetails?.group?.groupUuid != null,
    projectDetails,
    publicProjectDetails: publicProjectDetails as GetPublicProjectDetailsResponse | undefined,
    nextMilestone,
    pendingDeveloperInvitation,
    isPendingDeveloperInvitationLoading,
    navigateToPendingDeveloperInvitation,
    shouldRefreshPendingDeveloperInvitation,
    pendingProjectAccessRequest,
    isPendingProjectAccessRequestLoading,
    navigateToPendingProjectAccessRequest,
    shouldRefreshPendingProjectAccessRequest,
  };
};
